import { TrendingUp, BarChart } from "lucide-react";
import StandardSection from "./ui/StandardSection";
import { useScrollAnimation, useStaggeredAnimation } from "@/hooks/useScrollAnimation";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/utils/authContext";

const stats = [
  {
    value: "95%",
    label: "Campaign Success Rate",
    color: "text-primary-600",
  },
  {
    value: "2.3x",
    label: "Average ROI",
    color: "text-forest-custom",
  },
];

interface ROIUSPProps {
  isExploreEnabled: boolean;
  isLoginEnabled: boolean;
  base_path: string;
}

export default function ROIUSP({ isExploreEnabled, isLoginEnabled, base_path }: ROIUSPProps) {
  const navigate = useNavigate();

  const { isLoggedIn } = useAuth();
  const { ref: sectionRef, isVisible } = useScrollAnimation();
  const { ref: imageRef, isVisible: imageVisible } = useStaggeredAnimation(200);

  // Button logic
  const shouldShowButton =
    (isExploreEnabled && isLoginEnabled) ||
    (!isExploreEnabled && isLoginEnabled && isLoggedIn) ||
    (!isLoginEnabled && !isLoggedIn);

  const handleButtonClick = () => {
    localStorage.setItem('clearStartCampaignFilters', 'true');
    if (!isLoggedIn) {
      navigate(`${base_path}/explore`);
    } else {
      navigate(`${base_path}/propertylist`);
    }

  };


  return (
    <>
      <StandardSection background="white">
        <div
          ref={sectionRef}
          className={`grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-20 items-center px-4 sm:px-0 transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
        >
          <div
            ref={imageRef}
            className={`order-2 lg:order-1 relative transition-all duration-1000 ${imageVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"
              }`}
          >
            {/* Image with modern frame */}
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-br from-primary-600/20 to-forest-custom/20 rounded-3xl blur-xl group-hover:blur-2xl transition-all duration-500 animate-pulse"></div>
              <img
                src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&h=800"
                alt="Digital Marketing Analytics Dashboard"
                className="relative w-full rounded-3xl shadow-2xl border border-primary-200/50 group-hover:transform group-hover:scale-110 group-hover:rotate-1 transition-all duration-700 hover:shadow-emerald-200/50"
              />
              {/* Floating accent */}
              <div className="absolute -top-4 -right-4 w-8 h-8 bg-gradient-to-r from-primary-600 to-forest-custom rounded-full shadow-lg animate-bounce"></div>
            </div>
          </div>

          <div className="order-1 lg:order-2">
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-forest-100 to-primary-100 border border-forest-200 text-forest-700 px-6 py-3 rounded-full text-sm  mb-8 shadow-sm">
              <TrendingUp className="h-4 w-4 text-forest-600" />
              <span className="text-lg sm:text-xl">ROI Optimization</span>
              <BarChart className="h-4 w-4 text-primary-600" />
            </div>

            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold leading-tight FontStyles mb-3 sm:mb-4">
              <span className="text-gray-900 block">Get More Bang</span>
              <span className="bg-gradient-to-r from-primary-600 to-forest-custom text-primary-600 bg-clip-text block">for Your Buck</span>
            </h2>

            <p className="text-lg sm:text-xl text-gray-700 max-w-3xl mx-auto leading-normal mb-6 sm:mb-8">
              Consider disabling this temporarily as we do not provide analytics services with this platform other than displaying some audience data for consideration.
            </p>

            <div className="text-xl sm:text-2xl md:text-3xl text-gray-800 font-normal mb-4 sm:mb-6">
              Better ROI with Smart Analytics
            </div>

            <p className="text-lg sm:text-xl text-gray-800 mb-6 sm:mb-8 leading-normal">
              Due to the low margins and to build up our premium platform MAX, we should refrain from making strong claims about ROI on this tool.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
              {stats.map((stat, index) => (
                <div
                  key={index}
                  className="bg-white rounded-2xl p-4 sm:p-6 text-center shadow-lg border border-gray-100 hover:shadow-xl hover:scale-105 hover:bg-gradient-to-br hover:from-emerald-50 hover:to-green-50 transition-all duration-500 group cursor-pointer hover:border-emerald-200"
                >
                  <div
                    className={`text-2xl sm:text-3xl font-bold ${stat.color} mb-2 group-hover:scale-110 transition-transform duration-300`}
                  >
                    {stat.value}
                  </div>
                  <div className="text-gray-700 text-xs sm:text-sm font-medium group-hover:text-gray-800 transition-colors duration-300">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>

            {shouldShowButton && (
              <button
                onClick={handleButtonClick}
                className="w-full sm:w-auto px-4 py-3 sm:px-8 sm:py-4 h-11 text-base sm:text-lg font-semibold btn-primary-gradient hover:btn-primary-gradient text-white transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-emerald-500/40 transform hover:-translate-y-1 inline-flex items-center justify-center gap-2 rounded-md ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
                data-testid="button-find-high-impact-locations"
              >
                Find Your High-Impact Locations →
              </button>
            )}
          </div>
        </div>
      </StandardSection>
    </>
  );
}